import { Box, Button, styled, TextField, Typography } from "@mui/material";
import { CustomSelect } from "../../components-new/common";
import { RunTimeOptions } from "../Studio/constants";
import { TimeSeriesChart } from "../../components-new/charts";
import { useState, useEffect, useRef } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useExecutionThreadChartQuery } from "../../useQuery";
import { ExecutionThreadChartData } from "./Dummydata";

const ChartContainer = styled(Box)`
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0px -3px 17.1px 0px rgba(0, 0, 0, 0.07);
  margin-bottom: 36px;
  padding: 30px;
`;

const ChartWrapper = styled(Box)`
  position: relative;

  .custom-data-label {
    position: relative;
    background-color: #fff;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: #304758;
    border: 2px solid #304758;
  }

  .arrow_box {
    position: relative;
    border-radius: 9px;
    padding: 15px;
    box-shadow: 0px 2px 17.1px 0px #d1dbff;
    font-size: 12px;
    font-weight: 400;
  }

  .time-data {
    color: #ff5336;
  }

  .arrow_box:after,
  .arrow_box:before {
    right: 100%;
    top: 50%;
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }

  .arrow_box:after {
    border-color: rgba(85, 85, 85, 0);
    border-right-color: #d1dbff;
    border-width: 10px;
    margin-top: -10px;
  }
  .arrow_box:before {
    border-color: rgba(0, 0, 0, 0);
    border-right-color: #d1dbff;
    border-width: 13px;
    margin-top: -13px;
  }

  .apexcharts-tooltip {
    transform: translateX(10px) translateY(10px);
    overflow: visible !important;
    white-space: normal !important;
  }

  .vertical-line {
    position: absolute;
    top: 0;
    height: 100%;
    width: 2px;
    background-color: #304758;
    z-index: 10;
    pointer-events: none;
    transition: left 0.05s ease-out;
  }

  .line-handle {
    position: absolute;
    width: 12px;
    height: 12px;
    background-color: #304758;
    border-radius: 50%;
    top: 0;
    transform: translate(-50%, -50%);
    z-index: 11;
    cursor: col-resize;
  }

  .data-tooltip {
    position: absolute;
    background-color: white;
    border-radius: 8px;
    padding: 10px;
    box-shadow: 0px 2px 17.1px 0px #d1dbff;
    z-index: 12;
    min-width: 200px;
    max-width: 300px;
  }

  .chart-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 9;
    cursor: col-resize;
  }
`;

const ResetBtn = styled(Button)`
  color: #2b3539;
  font-size: 13px;
  font-weight: 300;
  text-decoration: underline;
`;

const ExecutionTestcaseRunChart = () => {
  const [timeopt, setTimeopt] = useState("03:00 - 04:00");
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [formattedData, setFormattedData] = useState([]);
  const [linePosition, setLinePosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipData, setTooltipData] = useState([]);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [currentTimestamp, setCurrentTimestamp] = useState(null);

  const chartRef = useRef(null);
  const chartOverlayRef = useRef(null);
  const verticaLineRef = useRef(null);
  const tooltipRef = useRef(null);
  const chartContentRef = useRef(null);

  const {
    data: apiData,
    refetch,
    isSuccess,
    isLoading,
  } = useExecutionThreadChartQuery({
  fromDate: fromDate.utc().format("YYYY-MM-DD HH:mm:ss"),
  toDate: toDate.utc().format("YYYY-MM-DD HH:mm:ss"),
});

  useEffect(() => {
    const now = dayjs();
    const currentHour = now.hour();
    const currentTimeRange = `${String(currentHour).padStart(2, "0")}:00 - ${String(currentHour + 1).padStart(2, "0")}:00`;
    setTimeopt(currentTimeRange);
    setFromDate(
      now
        .startOf("day")
        .hour(parseInt(currentTimeRange.split(" - ")[0].split(":")[0]))
        .minute(parseInt(currentTimeRange.split(" - ")[0].split(":")[1]))
        .second(0),
    );
    setToDate(
      now
        .startOf("day")
        .hour(parseInt(currentTimeRange.split(" - ")[1].split(":")[0]))
        .minute(parseInt(currentTimeRange.split(" - ")[1].split(":")[1]))
        .second(59),
    );
  }, []);

  useEffect(() => {
  if (!isSuccess || !apiData || !apiData[0]?.data) return;

  const MIN_DURATION_MS = 500;

  const normalizedData = apiData[0].data.map(item => {
    if (!Array.isArray(item?.y)) return item;

    const [start, end] = item.y;

    if (start === end) {
      return {
        ...item,
        y: [start, start + MIN_DURATION_MS],
        __aborted: true
      };
    }

    return item;
  });

  setFormattedData([
    {
      ...apiData[0],
      data: normalizedData
    }
  ]);

  setShowTooltip(false);
  setTooltipData([]);

}, [apiData, isSuccess]);


  useEffect(() => {
    if (fromDate && toDate && fromDate.format("HH:mm") !== toDate.format("HH:mm")) {
      refetch();
    }
  }, [fromDate, toDate, timeopt, refetch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (chartRef.current) {
        chartContentRef.current = chartRef.current.querySelector(".apexcharts-inner");

        const chartRect = chartRef.current.getBoundingClientRect();
        setLinePosition(chartRect.width / 2);

        updateLinePosition(chartRect.width / 2);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formattedData]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    if (chartOverlayRef.current) {
      const chartRect = chartOverlayRef.current.getBoundingClientRect();
      const newPosition = e.clientX - chartRect.left;
      updateLinePosition(newPosition);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();

    if (chartOverlayRef.current) {
      const chartRect = chartOverlayRef.current.getBoundingClientRect();
      const newPosition = Math.min(Math.max(0, e.clientX - chartRect.left), chartRect.width);
      updateLinePosition(newPosition);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const updateLinePosition = (position) => {
  setLinePosition(position);

  // ✅ HARD GUARDS
  if (
    !chartRef.current ||
    !Array.isArray(formattedData) ||
    formattedData.length === 0 ||
    !Array.isArray(formattedData[0]?.data)
  ) {
    setShowTooltip(false);
    return;
  }

  const chartRect = chartRef.current.getBoundingClientRect();
  if (chartRect.width === 0) return;

  const positionRatio = position / chartRect.width;

  const startMs = fromDate.toDate().getTime();
const endMs   = toDate.toDate().getTime();

const timeRange = endMs - startMs;
const timestamp = startMs + timeRange * positionRatio;


  setCurrentTimestamp(timestamp);

  // ✅ SAFE FILTER — this is the key fix
  const intersectingPoints = formattedData[0].data.filter((item) => {
    if (
      !item ||
      !Array.isArray(item.y) ||
      item.y.length !== 2 ||
      item.y[0] == null ||
      item.y[1] == null
    ) {
      return false;
    }

    return item.y[0] <= timestamp && item.y[1] >= timestamp;
  });

  if (intersectingPoints.length > 0) {
    setTooltipData(intersectingPoints);
    setShowTooltip(true);

    const tooltipTop = 10;
    let tooltipLeft = position;

    if (tooltipRef.current) {
      const tooltipWidth = tooltipRef.current.offsetWidth;
      if (position + tooltipWidth > chartRect.width) {
        tooltipLeft = position - tooltipWidth;
      }
    }

    setTooltipPosition({ top: tooltipTop, left: tooltipLeft });
  } else {
    setTooltipData([]);
    setShowTooltip(false);
  }
};


  const handleDateChange = (date) => {
    const newDate = dayjs(date);
    setFromDate(
      newDate
        .startOf("day")
        .hour(parseInt(timeopt.split(" - ")[0].split(":")[0]))
        .minute(parseInt(timeopt.split(" - ")[0].split(":")[1]))
        .second(0),
    );
    setToDate(
      newDate
        .startOf("day")
        .hour(parseInt(timeopt.split(" - ")[1].split(":")[0]))
        .minute(parseInt(timeopt.split(" - ")[1].split(":")[1]))
        .second(59),
    );
  };

  const handleTimeRangeChange = (event) => {
    const newTimeRange = event.target.value;
    setTimeopt(newTimeRange);
    const [startTime, endTime] = newTimeRange.split(" - ");
    const updatedFromDate = fromDate
      .hour(parseInt(startTime.split(":")[0]))
      .minute(parseInt(startTime.split(":")[1]))
      .second(0);
    const updatedToDate = fromDate
      .hour(parseInt(endTime.split(":")[0]))
      .minute(parseInt(endTime.split(":")[1]))
      .second(59);
    setFromDate(updatedFromDate);
    setToDate(updatedToDate);
  };

  const handleReset = () => {
    const now = dayjs();
    const currentHour = now.hour();
    const currentTimeRange = `${String(currentHour).padStart(2, "0")}:00 - ${String(currentHour + 1).padStart(2, "0")}:00`;
    setTimeopt(currentTimeRange);
    setFromDate(
      now
        .startOf("day")
        .hour(parseInt(currentTimeRange.split(" - ")[0].split(":")[0]))
        .minute(parseInt(currentTimeRange.split(" - ")[0].split(":")[1]))
        .second(0),
    );
    setToDate(
      now
        .startOf("day")
        .hour(parseInt(currentTimeRange.split(" - ")[1].split(":")[0]))
        .minute(parseInt(currentTimeRange.split(" - ")[1].split(":")[1]))
        .second(59),
    );

    setTimeout(() => refetch(), 0);
  };

  const formatTimestamp = (timestamp) => {
    return dayjs(timestamp).format("HH:mm:ss");
  };

  return (
    <ChartContainer my={3}>
      <Box display="flex" alignItems="center">
        <Box display="flex" justifyContent={"space-between"} alignItems="center" ml={2}>
          <Box display="flex" columnGap={3} alignItems={"center"}>
            <Box>
              <Typography fontSize={"13px"} fontWeight={500} color="#8089A8" mb={"6px"}>
                Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  inputFormat="MM/DD/YYYY"
                  value={fromDate}
                  onChange={handleDateChange}
                  renderInput={(params) => <TextField {...params} />}
                  sx={{
                    backgroundColor: "#fff",
                    boxShadow: "0px 17px 25px 0px rgba(0, 0, 0, 0.05)",
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box marginLeft={"17px"} minWidth={"170px"}>
              <CustomSelect
                options={RunTimeOptions}
                label="Select Test Cases Running Time"
                width={"100%"}
                background={"#ffffff"}
                value={timeopt}
                onChange={handleTimeRangeChange}
              />
            </Box>
            <Box mt={"26px"}>
              <ResetBtn variant="text" onClick={handleReset}>
                Reset
              </ResetBtn>
            </Box>
          </Box>
        </Box>
      </Box>
      <ChartWrapper ref={chartRef}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <Typography>Loading chart data...</Typography>
          </Box>
        ) : (
          <>
            <TimeSeriesChart series={formattedData} fromDate={fromDate} toDate={toDate} />

            {/* Chart overlay for mouse interaction */}
            {/* <Box className="chart-overlay" ref={chartOverlayRef} onMouseDown={handleMouseDown} />
             */}

             <Box
  className="chart-overlay"
  ref={chartOverlayRef}
  onMouseDown={handleMouseDown}
  onMouseMove={(e) => {
    const rect = chartOverlayRef.current.getBoundingClientRect();
    updateLinePosition(e.clientX - rect.left);
  }}
/>


            {/* Vertical line indicator */}
            <Box className="vertical-line" ref={verticaLineRef} sx={{ left: `${linePosition}px` }}>
              <Box className="line-handle" sx={{ left: 0 }} />
            </Box>

            {/* Data tooltip */}
            {showTooltip && (
              <Box
                className="data-tooltip"
                ref={tooltipRef}
                sx={{
                  top: tooltipPosition.top,
                  left: tooltipPosition.left,
                }}
              >
                {currentTimestamp && (
                  <Typography fontWeight={500} fontSize="14px" mb={1} color="#304758">
                    Time: {formatTimestamp(currentTimestamp)}
                  </Typography>
                )}

                {tooltipData.length > 0 ? (
                  tooltipData.map((item, index) => (
                    <Box key={index} mb={index < tooltipData.length - 1 ? 2 : 0}>
                      <Typography fontWeight={500} fontSize="13px">
                        {item.x}: {item.label}
                      </Typography>
                      <Typography className="time-data" fontSize="12px">
                        {formatTimestamp(item.y[0])} - {formatTimestamp(item.y[1])}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography fontSize="13px" color="#8089A8">
                    No test cases running at this time
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </ChartWrapper>
    </ChartContainer>
  );
};

export default ExecutionTestcaseRunChart;
